'use client';

import StickyNavigation from '@/components/common/StickyNavigation';
import ScrollProgressIndicator from '@/components/common/ScrollProgressIndicator';
import ConversionTracker from '@/components/common/ConversionTracker';
import AnchorLinkManager from '@/components/common/AnchorLinkManager';
import HeroSection from './HeroSection';
import ProductMotionDemo from './ProductMotionDemo';
import SampleBriefSection from './SampleBriefSection';
import TestimonialsSection from './TestimonialsSection';
import StartSection from './StartSection';
import FAQSection from './FAQSection';
import FooterSection from './FooterSection';
import { EducationalGuidesSection, MethodologySection, PopularToolsSection } from './ToolLedSections';
import { useAuth } from '@/contexts/AuthContext';
import { generateFAQSchema } from '@/lib/seo-utils';
import ScrollReveal from '@/components/ui/ScrollReveal';
import { trackGrowthEvent } from '@/lib/analytics';

const LandingPageInteractive = () => {
    const { isAuthenticated } = useAuth();

    const handleEngagementMilestone = (milestone: string, data: unknown) => {
        trackGrowthEvent('landing_engagement', { milestone, data });
    };

    const handleConversionAction = (action: string, data: unknown) => {
        trackGrowthEvent('landing_conversion', { action, data });
    };

    const handleSectionChange = (section: string, timeSpent: number) => {
        trackGrowthEvent('landing_section_view', { section, time_spent_ms: timeSpent });
    };

    // FAQ data for schema markup
    const faqItems = [
        {
            question: "What makes YourAura different from generic horoscope apps?",
            answer: "YourAura is a personal spiritual dashboard, not a single horoscope feed. It combines Daily Aura, horoscope, Vedic charts, numerology, palm reading, tarot, dreams, Full Pattern, compatibility, Planet Impact, Guidance, Day Decoder, saved readings, favorites, and community tools in one guided space."
        },
        {
            question: "How is my personal data protected?",
            answer: "We use service providers to run YourAura: Google Gemini processes inputs included in AI-assisted readings, Google Analytics measures site usage, and Razorpay processes payments. We do not sell personal information. Account deletion removes product records, while billing records and a deletion record are retained. See our privacy policy for details."
        },
        {
            question: "Can I create an account now?",
            answer: "Yes. YourAura is open to new users now. Create an account, verify your email, and open your private dashboard."
        },
        {
            question: "What plans are available?",
            answer: "You can start with a free account. Paid plans add deeper reports, history, favorites, and relationship or community features when enabled."
        },
        {
            question: "How does AI improve traditional astrology?",
            answer: "AI helps organize chart placements, numerology cycles, palm observations, tarot draws, dream themes, compatibility context, and Planet Impact signals into clear language. Traditional systems provide the structure; AI helps translate them into practical guidance."
        },
        {
            question: "Can I use YourAura without uploading palm photos?",
            answer: "Yes! Palm reading is optional. You'll still receive personalized daily insights based on your birth chart and numerology."
        },
        {
            question: "How long does it take to get my first reading?",
            answer: "Your first preview arrives right after signup and profile setup. From there, the dashboard opens into daily aura, horoscope, charts, numerology, palm reading, tarot, dreams, guidance, Day Decoder, history, favorites, and community features."
        },
        {
            question: "Is there a money-back guarantee?",
            answer: "Free account access does not require a refund. Paid plan terms are shown before checkout when you choose to upgrade."
        }
    ];

    const faqSchema = generateFAQSchema(faqItems);

    return (
        <div className="experience-page min-h-screen">
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
            />
            <StickyNavigation isAuthenticated={isAuthenticated} />
            <ScrollProgressIndicator showMilestones={true} />
            <ConversionTracker
                onEngagementMilestone={handleEngagementMilestone}
                onConversionAction={handleConversionAction}
                onSectionChange={handleSectionChange}
            />
            <AnchorLinkManager offset={80} duration={800} />

            <HeroSection isAuthenticated={isAuthenticated} />
            <ScrollReveal delay={0.04}><ProductMotionDemo /></ScrollReveal>
            <ScrollReveal delay={0.05}><SampleBriefSection /></ScrollReveal>
            <ScrollReveal delay={0.05}><PopularToolsSection /></ScrollReveal>
            <ScrollReveal delay={0.05}><MethodologySection /></ScrollReveal>
            <ScrollReveal delay={0.1}><TestimonialsSection /></ScrollReveal>
            <ScrollReveal delay={0.1}><EducationalGuidesSection /></ScrollReveal>
            <ScrollReveal delay={0.1}><FAQSection /></ScrollReveal>
            <ScrollReveal delay={0.1}><StartSection isAuthenticated={isAuthenticated} /></ScrollReveal>
            <ScrollReveal><FooterSection /></ScrollReveal>

        </div>
    );
};

export default LandingPageInteractive;
